/*
 * This is a mixin used by OptimizerInputOberserver.vue
 *
 */

import { mapGetters, mapActions } from 'vuex'
import { flatten_sequence } from '@/models/sequence/util'
import { mapReady } from '@/api/misc'
import { TRIGGER_OPTIMIZER_ON_INPUT_CHANGE } from '@/features'
import moment from '@/moment-gp'
import $models from '@/models'
import $registry from "@/generic_api/lib/generic_store/loader/registry"

const log1 = (...args) => { console.log(...args) }
const log2 = (...args) => { /* console.log(...args) */ }

export default {
  created() {

    if ( this.OPTIMIZER_DISABLE ) {
      console.log("[optimizer:sub] Disabled!")
      return
    }

    const input_can_be_prepared = (unwatch) => {
      log1("[optimizer:sub] Rrrrrrr... READY: input_can_be_prepared")
      this.optimizier_initial_subscribe();
      unwatch()
    }

    const optimizer_can_be_called = (unwatch) => {
      log1("[optimizer:sub] Rrrrrrr... READY: optimizer_can_be_called")
      this.optimizer_trigger()
      unwatch()

      /* from now on keep watching the input */
      log1("[optimizer:sub] Observing optimizer input from now on... ")

      this.$watch('optimizer_input', (new_input) => {
        if ( TRIGGER_OPTIMIZER_ON_INPUT_CHANGE ) {
          log1("WATCH:OPT_INPUT")

          /* HACK */
          if ( this.enableDebugMode ) {
            this.onNewOptimizerInput( new_input );
          }
          this.optimizer_trigger_debounced()
        }
      })
    }

    let unwatch1 = this.$watch('input_can_be_prepared', ready => {
      if ( ready ) { input_can_be_prepared(unwatch1) }
    })

    let unwatch2 = this.$watch('optimizer_can_be_called', ready => {
      if ( ready ) { optimizer_can_be_called(unwatch2) }
    })

    if ( this.input_can_be_prepared ) { input_can_be_prepared(unwatch1) }
    if ( this.optimizer_can_be_called ) { optimizer_can_be_called(unwatch2) }

  },
  computed: {
    ...mapReady(
      'goals',
      'commitments',
      'timeslot_collections',
      'timeslot_worker',
      'optimizer_worker',
      'optimizer_input'
    ),

    ...mapGetters('optimizer', [
      'optimizer_input_commitments',
      'optimizer_input_related_goal_list',
      'optimizer_manual_dayplan',
    ]),
    ...mapGetters('app', [
      'OPTIMIZER_DISABLE'
    ]),
    ...mapGetters('session', [
      'session_user_timer'
    ]),

    timeslot_collection_list() {
      return this.$models.timeslotCollection.subscribeList().items;
    },

    timeslot_exceptions() {
      return this.$models.timeslotException.subscribeList().items;
    },

    timeslot_weekday_prefs() {
      return this.$models.timeslotWeekdayPref.subscribeList().items;
    },

    input_can_be_prepared() {
      if ( this.OPTIMIZER_DISABLE ) {
        console.log("[optimizer:sub] Disabled (input_can_be_prepared)")
        return
      }

      return this.$ready_goals
          && this.$ready_timeslot_collections
          && this.$ready_commitments
          && this.$ready_optimizer_worker
          && this.$ready_timeslot_worker
    },

    optimizer_can_be_called() {
      if ( this.OPTIMIZER_DISABLE ) {
        console.log("[optimizer:sub] Disabled (optimizer_can_be_called)")
        return
      }
      return this.$ready_optimizer_input;
    },

    session_user_timer: (state, getters, rootState, rootGetters) => {
      if ($models.user.defaultUserId) {
        return $models.userTimer.subscribeNode( $models.user.defaultUserId )
      } else {
        return null
      }
    }
  },
  watch: {
    "optimizer_input_related_goal_list": {
      handler( related_goal_list ) {
        if ( !this.input_can_be_prepared ) { return } // TODO: RAUS?
        log1("OPTIMIZER:INPUT:TRIGGER:1 [optimizer_input_related_goal_list]")
        this.optimizer_input_existing_events__subscribe( related_goal_list )
        this.optimizer_input_tasks__subscribe( related_goal_list )
        this.optimizer_input_commitment_settings__subscribe( related_goal_list )
        this.optimizer_input_recorded_sessions__subscribe()
      }
    },

    'optimizer_input_commitments': {
      handler({ commitment_list }) {
        if ( !this.input_can_be_prepared ) { return } // TODO: RAUS?
        log1("OPTIMIZER:INPUT:TRIGGER:2 [optimizer_input_commitments]")
        this.optimizer_input_sequences__subscribe( commitment_list )
      },
      // deep: true ??
    },

    'timeslot_collection_list': {
      handler() {
        if ( !this.input_can_be_prepared ) { return }
        log1("OPTIMIZER:INPUT:TRIGGER:3 [timeslot_collection_list]")
        this.optimizer_input_tsc_data__subscribe()
      }
    },

    'timeslot_exceptions': {
      handler() {
        if ( !this.input_can_be_prepared ) { return }
        log1("OPTIMIZER:INPUT:TRIGGER:3a [timeslot_exceptions]")
        this.optimizer_input_tsc_data__subscribe()
      },
      deep: true
    },

    'timeslot_weekday_prefs': {
      handler() {
        if ( !this.input_can_be_prepared ) { return }
        log1("OPTIMIZER:INPUT:TRIGGER:3b [timeslot_weekday_prefs]")
        this.optimizer_input_tsc_data__subscribe()
      },
      deep: true
    },

    'session_user_timer': {
      handler(test) {
        log1("OPTIMIZER:INPUT:TRIGGER:4 [session_user_timer]")
        if (this.session_user_timer.$ready) {
          this.session_restart_timer()
        }
      }
    },

    'optimizer_manual_dayplan': {
      handler({ dayplan_list }) {
        // Nothing to do ...
        // In the future we'll do a optimizer_manual_dayplan__subscribe here
      },
      // deep: true ??
    },
  },
  methods: {
    ...mapActions('session', [
      'session_restart_timer'
    ]),
    /* */
    optimizier_initial_subscribe() {
      const related_goal_list = this.optimizer_input_related_goal_list
      const { commitment_list } = this.optimizer_input_commitments

      let promiseTimeslotWorker     = this.optimizer_input_tsc_data__subscribe()
      let promiseTask               = this.optimizer_input_tasks__subscribe( related_goal_list )
      let promiseCommitmentSettings = this.optimizer_input_commitment_settings__subscribe( related_goal_list )
      let promiseSequences          = this.optimizer_input_sequences__subscribe( commitment_list )
      let promiseExistingEvents     = this.optimizer_input_existing_events__subscribe( related_goal_list )
      let promiseRecordedSessions   = this.optimizer_input_recorded_sessions__subscribe()

      /* sequences and timeslots (from worker) are ready - merge results */
      return Promise.all([
        promiseTimeslotWorker,
        promiseTask,
        promiseCommitmentSettings,
        promiseSequences,
        promiseExistingEvents,
        promiseRecordedSessions
      ]).then(result => {
        /*
        let tsc_data_array   = result[0]
        let tasks_by_goal_id = result[1]
        let sequences        = result[2]
        let existing_events  = result[3]
        */
        this.$api.set_ready('optimizer_input');
      });
    },

    /* */
    optimizer_input_existing_events__subscribe(related_goal_list) {
      // Should also trigger, when realted_goal_list is updated
      let promiseListExistingEvents = Object.keys( related_goal_list ).map(goalId => {
        let existing_events_from_db = this.$models.commitmentSession
          .with({ goalId })
          .subscribeList()

        return existing_events_from_db.$promise;
      });
      log2("[optimizer:sub] promise list existing events", promiseListExistingEvents)
      return promiseListExistingEvents
    },

    /* */
    optimizer_input_tsc_data__subscribe() {

      // TODO !
      const planning_horizon = this.$store.getters['optimizer/optimizer_planning_horizon']
      const range_start = moment.currentTime(); // TODO: has to be in correct timezone !

      //let timeslot_flat_by_tsc = getters.flattened_calendar_data_all_with_start_and_horizon(range_start, planning_horizon)
      const worker = this.$store.getters['timeslots/worker_flattened_calendar_data_with_start_and_horizon'];
      const promiseTimeslotWorker = worker(range_start, planning_horizon).then( data => {
        // let timeslot_flat_by_tsc = data;
        log2("[optimizer:sub] SET_TIMESLOT_CACHE", data)
        this.$store.commit('timeslots/SET_TIMESLOT_CACHE', data)
      }).catch((error) => {
        console.warn("[optimizer:input] timeslots: Got error from worker", error)
        throw new Error('Can not calulate timeslots: ' + error.message)
      });
      return promiseTimeslotWorker
    },

    /* Get sequences (async + concurrently with webworker) */
    optimizer_input_sequences__subscribe(commitment_list) {
      // const { commitment_list } = getters.optimizer_input_commitments
      let promiseListSequences = commitment_list.map(commitment => {
        if ( commitment.type !== 'SEQUENCE' || !commitment.sequenceId ) {
          return
        }
        let sequenceModel = this.$models.sequence.subscribeNode( commitment.sequenceId );
        return sequenceModel.$promise.then(() => {

          // DOPPELT (is it necessary)
          let flat_sequence = flatten_sequence( sequenceModel.$state.sequenceItemList, /*false*/ );

          let promiseListSeqTasks = flat_sequence.map(sequenceItem => {
            if ( sequenceItem.type === "SESSION:TASK" ) {

              if ( !sequenceItem.goalId || !sequenceItem.memberId || !sequenceItem.taskId) {
                return
              }

              /*
              let task = this.$models.task.with({
                  goalId: sequenceItem.goalId,
                  memberId: sequenceItem.memberId
                }).getNodeFull( sequenceItem.taskId )
              */

              let task = this.$models.taskMeta.with({
                goalId: sequenceItem.goalId,
                uid:    sequenceItem.memberId,
              }).subscribeNode( sequenceItem.taskId )

              return task.$promise;
            }
          });

          return promiseListSeqTasks
        });
      });

      return Promise.all( promiseListSequences )
    },

    /* Get tasks (async +but not concurrently with webworker) */
    optimizer_input_tasks__subscribe(related_goal_list) {
      let promiseListTasks =
        Object.keys( related_goal_list ).map(goalId => {
          return this.$models.task
            .with({ goalId })
            .get_lane_task_list(true) // Get only tasks for current user
        })
      let promiseTask = Promise.all( promiseListTasks )
      return promiseTask
    },

    /* Get commitmentSettings (async +but not concurrently with webworker) */
    optimizer_input_commitment_settings__subscribe(related_goal_list) {
      let promiseListCommitmentSettings =
        Object.keys( related_goal_list ).map(goalId => {
          return this.$models.commitment
            .with({ goalId })
            .getters
            .commitment_settings_current_user
        })
      let promiseCommitmentSettings = Promise.all( promiseListCommitmentSettings )
      return promiseCommitmentSettings
    },

    /* Get recorded sessions (async +but not concurrently with webworker) */
    optimizer_input_recorded_sessions__subscribe() {
      let loaded_goal_id_list = $registry.state.res.goal
          ? Object.keys( $registry.state.res.goal )
          : []

      loaded_goal_id_list = loaded_goal_id_list.filter(goalId => {
        let goal_meta = $registry.state.res.goal[ goalId ].meta;

        /* Check if goal exists and if we have access rights */
        if ( !goal_meta || goal_meta[ '.exists' ] === false || goal_meta[ '.noaccess' ] === true ) {
          console.log("Goal: No access or does not exist", goalId)
          return false;
        }

        return true;
      })

      let promiseListRecordedSessions = loaded_goal_id_list.map(goalId => {
        let recorded_sessions_from_db = this.$models.taskRecordedSession
          .with({
            goalId: goalId
          })
          .subscribeList()
        return recorded_sessions_from_db.$promise;
      })
      return Promise.all( promiseListRecordedSessions )
    }
  }
}
